/**
 * Демонстрационные страницы для отработки attachment lifecycle.
 * При форке скелета удалить или модифицировать под конкретное приложение.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ActionPanel, Button, DocumentBlock, FileUpload, Paper } from '../uikit';
import type { DocumentItem, UploadedFile } from '../uikit';
import type { ProductImage } from '../api/products';
import {
  createProduct,
  updateProduct,
  findProductsByIDs,
  getCategories,
  isProductDocumentsViolation,
  isProductImageViolation,
  toProductImage,
  userMessageForProductDocumentsViolation,
  userMessageForProductImageViolation,
} from '../api/products';
import { ApiError, parseApiViolations, toUserMessage } from '../api/client';
import type { ApiViolation } from '../api/client';

const DECIMAL_2_RE = /^-?(\d+([.,]\d{0,2})?)?$/;

function normalizeDecimal(value: string): string {
  return value.replace(',', '.').trim();
}

function parseDecimal(value: string): number | null {
  if (!value) {
    return null;
  }
  const normalized = normalizeDecimal(value);
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

const FORM_FIELD_ORDER = [
  'product-name',
  'product-cost-price',
  'product-sale-price',
  'product-category',
  'product-description',
  'product-image',
  'product-documents-upload',
] as const;

function isMappedProductFormViolation(v: ApiViolation): boolean {
  const path = v.propertyPath.trim();
  if (isProductDocumentsViolation(v) || isProductImageViolation(v)) {
    return true;
  }
  return (
    path === 'name' ||
    path === 'description' ||
    path === 'category' ||
    path === 'costPrice' ||
    path === 'salePrice'
  );
}

function focusFirstInvalidField(fieldOrder: readonly string[]): void {
  const firstInvalidId = fieldOrder.find(
    (fieldId) => document.getElementById(fieldId)?.getAttribute('aria-invalid') === 'true',
  );
  if (!firstInvalidId) return;
  const el = document.getElementById(firstInvalidId) as
    | HTMLInputElement
    | HTMLTextAreaElement
    | HTMLSelectElement
    | null;
  el?.focus();
}

const FormField = styled.div`
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space[1]}px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FormInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  max-width: 400px;
  padding: ${({ theme }) => theme.space[1]}px 12px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-family: inherit;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.divider)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const FormInputShort = styled(FormInput)`
  max-width: 200px;

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const FormTextarea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  max-width: 400px;
  padding: ${({ theme }) => theme.space[1]}px 12px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-family: inherit;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.divider)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text.primary};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const ErrorMessage = styled.div`
  margin-top: ${({ theme }) => theme.space[0]}px;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.error.main};
`;

const PageTitle = styled.h2`
  margin: 0 0 24px;
  font-size: ${({ theme }) => theme.fontSizes[9]}px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LoadingText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[1]}px;
  flex-wrap: wrap;

  ${({ theme }) => theme.device.mobile} {
    & > button {
      width: 100%;
    }
  }
`;

export function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [image, setImage] = useState<UploadedFile | null>(null);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [nameError, setNameError] = useState<string | null>(null);
  const [costPriceError, setCostPriceError] = useState<string | null>(null);
  const [salePriceError, setSalePriceError] = useState<string | null>(null);
  const [categoryFieldError, setCategoryFieldError] = useState<string | null>(null);
  const [descriptionFieldError, setDescriptionFieldError] = useState<string | null>(null);
  const [imageFieldError, setImageFieldError] = useState<string | null>(null);
  const [documentsFieldError, setDocumentsFieldError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const initialSnapshotRef = useRef<{
    name: string;
    description: string;
    category: string;
    costPrice: string;
    salePrice: string;
    imageId: string | null;
    documentsHash: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setFetchLoading(true);
    setLoadError(null);
    findProductsByIDs([id])
      .then((res) => {
        if (!cancelled) {
          const p = res.items[id];
          if (p) {
            setName(p.name);
            setDescription(p.description ?? '');
            setCategory(p.category ?? '');
            setCostPrice(p.costPrice ?? '');
            setSalePrice(p.salePrice ?? '');
            if (p.image) {
              setImage({
                id: p.image.id,
                uri: p.image.uri,
                filename: p.image.filename,
                size: p.image.size,
              });
            }
            const productDocuments = p.documents ?? [];
            setDocuments(
              productDocuments.map((d) => ({
                file: {
                  id: d.file.id,
                  uri: d.file.uri,
                  filename: d.file.filename,
                  size: d.file.size,
                },
              })),
            );
            initialSnapshotRef.current = {
              name: p.name,
              description: p.description ?? '',
              category: p.category ?? '',
              costPrice: p.costPrice ?? '',
              salePrice: p.salePrice ?? '',
              imageId: p.image?.id ?? null,
              documentsHash: JSON.stringify(productDocuments),
            };
          } else {
            setLoadError('Товар не найден');
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(toUserMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const isDirty = useCallback(() => {
    const snap = initialSnapshotRef.current;
    const documentsHash = JSON.stringify(
      documents.map((d) => ({ id: d.file.id, filename: d.file.filename })),
    );
    if (!snap && (name || description || category || costPrice || salePrice || image || documents.length > 0)) {
      return true;
    }
    if (!snap) return false;
    return (
      name !== snap.name ||
      description !== snap.description ||
      category !== snap.category ||
      costPrice !== snap.costPrice ||
      salePrice !== snap.salePrice ||
      (image?.id ?? null) !== snap.imageId ||
      documentsHash !== snap.documentsHash
    );
  }, [name, description, category, costPrice, salePrice, image, documents]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty() || loading) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, loading]);

  const handleCostPriceChange = (nextValue: string) => {
    if (nextValue === '' || DECIMAL_2_RE.test(nextValue)) {
      setCostPrice(nextValue);
      if (costPriceError) setCostPriceError(null);
    }
  };

  const handleSalePriceChange = (nextValue: string) => {
    if (nextValue === '' || DECIMAL_2_RE.test(nextValue)) {
      setSalePrice(nextValue);
      if (salePriceError) setSalePriceError(null);
    }
  };

  const validateCostPrice = (): boolean => {
    const parsed = parseDecimal(costPrice);
    if (parsed === null && costPrice !== '') {
      setCostPriceError('Введите число, например 123.45');
      return false;
    }
    if (parsed !== null && parsed < 0) {
      setCostPriceError('Себестоимость не может быть отрицательной');
      return false;
    }
    setCostPriceError(null);
    return true;
  };

  const validateSalePrice = (): boolean => {
    const parsed = parseDecimal(salePrice);
    if (parsed === null && salePrice !== '') {
      setSalePriceError('Введите число, например 123.45');
      return false;
    }
    if (parsed !== null && parsed < 0) {
      setSalePriceError('Цена продажи не может быть отрицательной');
      return false;
    }
    setSalePriceError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(null);
    setCostPriceError(null);
    setSalePriceError(null);
    setCategoryFieldError(null);
    setDescriptionFieldError(null);
    setImageFieldError(null);
    setDocumentsFieldError(null);
    setServerError(null);

    const hasNameError = !name.trim();
    if (hasNameError) setNameError('Введите наименование');

    const costPriceValid = validateCostPrice();
    const salePriceValid = validateSalePrice();

    if (hasNameError || !costPriceValid || !salePriceValid) {
      focusFirstInvalidField(FORM_FIELD_ORDER);
      return;
    }

    setLoading(true);
    try {
      const imagePayload: ProductImage | null = image
        ? toProductImage(image, 'images')
        : null;

      const documentsPayload = documents.map((d) => ({ file: d.file }));

      const payload = {
        name: name.trim(),
        description: description.trim(),
        category: category.trim(),
        costPrice: costPrice.trim(),
        salePrice: salePrice.trim(),
        image: imagePayload,
        documents: documentsPayload,
      };

      if (isEdit && id) {
        await updateProduct({ id, ...payload });
        navigate(`/products/${id}`);
      } else {
        await createProduct(payload);
        navigate('/products');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.details) {
        const violations = parseApiViolations(err.details);
        const documentViolations = violations.filter(isProductDocumentsViolation);
        const imageViolations = violations.filter(isProductImageViolation);

        if (documentViolations.length > 0) {
          const texts = [
            ...new Set(documentViolations.map((v) => userMessageForProductDocumentsViolation(v))),
          ];
          setDocumentsFieldError(texts.join(' '));
        }
        if (imageViolations.length > 0) {
          setImageFieldError(userMessageForProductImageViolation(imageViolations[0]!));
        }

        const nameViolation = violations.find((v) => v.propertyPath.trim() === 'name');
        if (nameViolation) {
          setNameError(nameViolation.message.trim());
        }
        const descriptionViolation = violations.find((v) => v.propertyPath.trim() === 'description');
        if (descriptionViolation) {
          setDescriptionFieldError(descriptionViolation.message.trim());
        }
        const categoryViolation = violations.find((v) => v.propertyPath.trim() === 'category');
        if (categoryViolation) {
          setCategoryFieldError(categoryViolation.message.trim());
        }
        const costViolation = violations.find((v) => v.propertyPath.trim() === 'costPrice');
        if (costViolation) {
          setCostPriceError(costViolation.message.trim());
        }
        const saleViolation = violations.find((v) => v.propertyPath.trim() === 'salePrice');
        if (saleViolation) {
          setSalePriceError(saleViolation.message.trim());
        }

        const unknownViolations = violations.filter((v) => !isMappedProductFormViolation(v));
        if (violations.length === 0) {
          setServerError(toUserMessage(err));
        } else if (unknownViolations.length > 0) {
          const mappedSome = violations.some(isMappedProductFormViolation);
          setServerError(mappedSome ? null : toUserMessage(err));
        } else {
          setServerError(null);
        }

        queueMicrotask(() => focusFirstInvalidField(FORM_FIELD_ORDER));
      } else {
        setServerError(toUserMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <LoadingText>Загрузка…</LoadingText>;
  }

  if (isEdit && loadError) {
    return (
      <>
        <ActionPanel defaultPath="/products" />
        <Paper padding={24} shadow={2} style={{ marginTop: 16 }}>
          <PageTitle>Редактировать товар</PageTitle>
          <ErrorMessage role="alert" aria-live="polite" style={{ marginBottom: 16 }}>
            {loadError}
          </ErrorMessage>
          <ButtonRow>
            <Button type="button" variant="filled" color="primary" onClick={() => setReloadKey((prev) => prev + 1)}>
              Повторить
            </Button>
          </ButtonRow>
        </Paper>
      </>
    );
  }

  return (
    <Paper padding={24} shadow={2}>
      <PageTitle>{isEdit ? 'Редактировать товар' : 'Новый товар'}</PageTitle>
      <form onSubmit={handleSubmit}>
        <FormField>
          <FormLabel htmlFor="product-name">
            Наименование *
          </FormLabel>
          <FormInput
            id="product-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            placeholder="Название товара"
            autoComplete="off"
            aria-required
            aria-invalid={Boolean(nameError)}
            aria-describedby={nameError ? 'product-name-error' : undefined}
            $error={Boolean(nameError)}
          />
          {nameError && (
            <ErrorMessage id="product-name-error" role="alert">
              {nameError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="product-category">Категория</FormLabel>
          <FormInput
            id="product-category"
            type="text"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              if (categoryFieldError) setCategoryFieldError(null);
            }}
            list="product-categories"
            placeholder="Выберите или введите категорию"
            enterKeyHint="next"
            aria-invalid={Boolean(categoryFieldError)}
            aria-describedby={categoryFieldError ? 'product-category-error' : undefined}
            $error={Boolean(categoryFieldError)}
          />
          {categoryFieldError ? (
            <ErrorMessage id="product-category-error" role="alert">
              {categoryFieldError}
            </ErrorMessage>
          ) : null}
          <datalist id="product-categories">
            {categories.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </FormField>

        <FormField>
          <FormLabel htmlFor="product-cost-price">Себестоимость, руб</FormLabel>
          <FormInputShort
            id="product-cost-price"
            type="text"
            inputMode="decimal"
            enterKeyHint="next"
            placeholder="0.00"
            value={costPrice}
            onChange={(e) => handleCostPriceChange(e.target.value)}
            onBlur={validateCostPrice}
            aria-invalid={Boolean(costPriceError)}
            aria-describedby={costPriceError ? 'product-cost-price-error' : undefined}
            $error={Boolean(costPriceError)}
          />
          {costPriceError && (
            <ErrorMessage id="product-cost-price-error" role="alert">
              {costPriceError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="product-sale-price">Цена продажи, руб</FormLabel>
          <FormInputShort
            id="product-sale-price"
            type="text"
            inputMode="decimal"
            enterKeyHint="next"
            placeholder="0.00"
            value={salePrice}
            onChange={(e) => handleSalePriceChange(e.target.value)}
            onBlur={validateSalePrice}
            aria-invalid={Boolean(salePriceError)}
            aria-describedby={salePriceError ? 'product-sale-price-error' : undefined}
            $error={Boolean(salePriceError)}
          />
          {salePriceError && (
            <ErrorMessage id="product-sale-price-error" role="alert">
              {salePriceError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="product-description">Описание</FormLabel>
          <FormTextarea
            id="product-description"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (descriptionFieldError) setDescriptionFieldError(null);
            }}
            rows={4}
            placeholder="Описание товара"
            enterKeyHint="done"
            aria-invalid={Boolean(descriptionFieldError)}
            aria-describedby={descriptionFieldError ? 'product-description-error' : undefined}
            $error={Boolean(descriptionFieldError)}
          />
          {descriptionFieldError ? (
            <ErrorMessage id="product-description-error" role="alert">
              {descriptionFieldError}
            </ErrorMessage>
          ) : null}
        </FormField>

        <FormField>
          <FormLabel htmlFor="product-image">Изображение</FormLabel>
          <FileUpload
            id="product-image"
            section="images"
            value={image}
            onChange={(next) => {
              setImage(next);
              if (imageFieldError) setImageFieldError(null);
            }}
            showPreview={true}
            serverError={imageFieldError}
          />
        </FormField>

        <FormField>
          <FormLabel id="product-documents-label">Документы</FormLabel>
          <div role="group" aria-labelledby="product-documents-label">
            <DocumentBlock
              id="product-documents"
              value={documents}
              onChange={(next) => {
                setDocuments(next);
                if (documentsFieldError) setDocumentsFieldError(null);
              }}
              disabled={loading}
              error={documentsFieldError}
            />
          </div>
        </FormField>

        {serverError && (
          <ErrorMessage role="alert" aria-live="polite" style={{ marginBottom: 16 }}>
            {serverError}
          </ErrorMessage>
        )}

        <ButtonRow>
          <Button type="submit" variant="filled" color="primary" loading={loading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={() => navigate(isEdit && id ? `/products/${id}` : '/products')}
          >
            Отмена
          </Button>
        </ButtonRow>
      </form>
    </Paper>
  );
}
